import { LinkProps, Theme, Tooltip } from "@mui/material";
import withStyles from '@mui/styles/withStyles';
import { GridCellParams, GridCellValue, GridColumnHeaderParams, GridEnrichedColDef, GridSortCellParams, GridSortModel } from "@mui/x-data-grid";
import { inject, injectable } from "inversify";
import React from "react";
import type { IpfModule } from "../..";
import { Constants } from "../../constants.generated";
import { DataGridColumnType, ListSortDirection } from "../../enums.generated";
import { EventConstants } from "../../EventConstants";
import { callFunction } from "../../functions/call-function";
import { doWhen } from "../../functions/do-when";
import { getProperty, getTypedProperty } from "../../functions/get-property";
import { isNullOrUndefined } from "../../functions/is-null-or-undefined";
import { addClass, dataFormat, urlDataFormat } from "../../functions/string";
import { container } from "../../ioc/container.config";
import { TYPES } from "../../ioc/types";
import { Icon } from "../../models/app";
import { BuildRowLinkTemplateArgs, DataGridColumnOptions, DataGridOptions, DataGridRefreshArgs, DataGridState } from "../../models/data-grid";
import { ApiUrl } from "../../models/http";
import { ApiModel, FieldSort } from "../../models/search";
import type { AjaxService, DataGridService, FormService, SearchFilterService, UrlService } from "../../services";
import { ServiceBase } from "../../services/ServiceBase";
import { Guid } from "../../util/Guid";
import { AppTheme } from "../AppTheme";
import { DataGridComponent } from "../components/DataGridComponent";
import { ReactDOMProxy } from "../ReactDOMProxy";

/**
 * A MUI styled Tooltip for use on data grids.
 */
const LightTooltip = withStyles((theme: Theme) => ({
	tooltip: {
		backgroundColor: AppTheme.palette.background.paper,
		color: AppTheme.palette.text.primary,
		boxShadow: theme.shadows[2],
		fontSize: 12,
		fontWeight: "normal",
		marginTop: 0
	},
}))(Tooltip);

/**
 * MUI data grid service, used to render MUI grids to specific elements on a page.
 */
@injectable()
export class MaterialDataGridService extends ServiceBase implements DataGridService {

	private static readonly iconCellClass = "icon";

	/**
	 * Map of cell rendering functions which return a React element for rendering, mapped by DataGridColumnType.
	 * @see DataGridColumnType
	 */ 
	private static readonly cellRenderers = new Map<DataGridColumnType, (column: DataGridColumnOptions, params: GridCellParams) => JSX.Element>([
		[DataGridColumnType.Boolean, MaterialDataGridService.renderBool],
		[DataGridColumnType.NegativeBoolean, MaterialDataGridService.renderNegativeBool],
		[DataGridColumnType.Formatted, MaterialDataGridService.renderFormatted],
		[DataGridColumnType.Html, MaterialDataGridService.renderHtml],
		[DataGridColumnType.HyperLink, MaterialDataGridService.renderHyperLink],
		[DataGridColumnType.Icon, MaterialDataGridService.renderIcon],
		[DataGridColumnType.MailTo, MaterialDataGridService.renderMailTo],
		[DataGridColumnType.Tooltip, MaterialDataGridService.renderTooltip],
		[DataGridColumnType.ConditionalClass, MaterialDataGridService.renderConditionalClass],
		[DataGridColumnType.Badge, MaterialDataGridService.renderBadge],
	]);

	/**
	 * Map of cell formatter functions which format the columns by adding CSS classes, mapped by DataGridColumnType.
	 * @see DataGridColumnType
	 */ 
	private static readonly cellFormatters = new Map<DataGridColumnType, (column: GridEnrichedColDef) => void>([
		[DataGridColumnType.Boolean, MaterialDataGridService.formatIcon],
		[DataGridColumnType.Financial, MaterialDataGridService.formatFinancial],
		[DataGridColumnType.Icon, MaterialDataGridService.formatIcon]
	]);

	private static readonly gridRefreshers = new Map<string, (args: DataGridRefreshArgs) => void>();

	// static renderer needs access to the UrlService singleton
	private static _urlService: UrlService;

	/**
	 * Initialises a new instance of the MaterialDataGridService class.
	 * @param ajaxService AJAX service, used to make AJAX HTTP requests.
	 * @param formService Form service, used to bind the client side view model.
	 * @param ipfModule IPF module, containing contextual information and functions.
	 * @param filterService Search filter service, used to render controls, for filtering data grids.
	 * @param urlService URL service, used to generate URLs.
	 */
	constructor(
		@inject(TYPES.AjaxService) private ajaxService: AjaxService,
		@inject(TYPES.FormService) private formService: FormService,
		@inject(TYPES.IpfModule) private ipfModule: IpfModule,
		@inject(TYPES.SearchFilterService) private filterService: SearchFilterService,
		@inject(TYPES.UrlService) private urlService: UrlService) {
		super();
	}

	/**
	 * Gets the URL service. This is static so the static renderer functions can access it.
	 */
	private static get urlServiceStatic() {
		if (!MaterialDataGridService._urlService) {
			MaterialDataGridService._urlService = container.get<UrlService>(TYPES.UrlService);
		}
		return MaterialDataGridService._urlService;
	}

	/**
	 * @inheritdoc
	 */
    public applyAll(target: Element) {

		target.querySelectorAll("[data-grid]").forEach(element => {
			this.render(element);
		});
	}

	/**
	 * @inheritdoc
	 */
	public refresh(elementId: string) {

		const gridElement = document.getElementById(elementId);

		if (!gridElement) return;

		const refresher = MaterialDataGridService.gridRefreshers.get(elementId);

		if (refresher) {

			refresher({ resetPageIndex: true });
		}
	}

	/**
	 * Renders a grid within the supplied target element.
	 * The element ID is used to lookup the IPF data containing the data grid configuration.
	 * @param element The target element with an ID attribute that matches the IPF data key of the data grid configuration.
	 */
	private render(element: Element) {

		const gridOptions = this.ipfModule.getData<DataGridOptions>(element.id);
		const sortModel = this.convertSortModel(gridOptions.apiModel?.sorts ?? gridOptions.fieldSorts, gridOptions.columns);
		const getData = (apiUrl: ApiUrl, options: DataGridOptions, state: DataGridState) => {
			return this.getData(apiUrl, options, state);
		};
		const onRendering = (options: DataGridOptions, state: DataGridState, refresh: (args: DataGridRefreshArgs) => void, redraw: () => void) => {
			return this.onRendering(options, state, refresh, redraw);
		};
		const onRendered = (options: DataGridOptions, state: DataGridState, refresh: (args: DataGridRefreshArgs) => void) => {
			return this.onRendered(options, state, refresh);
		};

		if (!gridOptions.keyField) {
			gridOptions.keyField = "id";
		}

		// render data grid component with configured options and event handlers
		ReactDOMProxy.renderStyled(
			<DataGridComponent
				columns={this.convertColumns(gridOptions)}
				getData={getData}
				onRendered={onRendered}
				onRendering={onRendering}
				options={gridOptions}
				sortModel={sortModel}
				element={element} />,
			element
		);
	}

	/**
	 * An event handler called when during rendering. Used to resize columns, setup filters and apply tear down functions.
	 * @param options The data grid options used to configure the grid.
	 * @param state The current data grid state.
	 * @param refresh A function used to refresh the data grid.
	 * @param redraw A function used to redraw the grid when columns are auto sized and the window is resized.
	 */
	private onRendering(options: DataGridOptions, state: DataGridState, refresh: (args: DataGridRefreshArgs) => void, redraw: () => void) {

		const tearDownFuncs = new Array<() => void>();

		// auto size the columns
		if (options.autoSizeColumns) {
			tearDownFuncs.push(this.sizeColumns(state.columns, options.elementId, redraw));
		}

		const filterElementId = options.filtersElementId;

		// setup filters if there is an API model with filters defined
		if (filterElementId) {

			const filtersElement = document.getElementById(filterElementId);
			if (filtersElement) {

				tearDownFuncs.push(this.filterService.setupFilters(filtersElement, refresh));
			}
		}

		return tearDownFuncs;
	}

	/**
	 * An event handler called after the data grid has been rendered. Used to apply additional behaviours not supported by MUI data grids.
	 * @param options The data grid options used to configure the grid.
	 * @param state The current data grid state.
	 * @param refresh A function used to refresh the data grid.
	 */
	private onRendered(options: DataGridOptions, state: DataGridState, refresh: (args: DataGridRefreshArgs) => void) {

		const gridElement = document.getElementById(options.elementId);

		if (!gridElement) return () => { }; 

		MaterialDataGridService.gridRefreshers.set(options.elementId, refresh);

		doWhen(() => {
			// this useEffect callback seems to be called before the grid has actually been displayed when changing paging parameters, so a timeout is required.
			global.setTimeout(() => {
				this.applyRowClass(options, state, gridElement),
				this.applyFinancialColumnAlignment(gridElement);
				this.applyDetailRow(options, state, gridElement);
				this.applyActivateOnRowClick(options, state, gridElement);
			}, 50);
		}, () => gridElement.querySelectorAll(".MuiDataGrid-virtualScrollerRenderZone .MuiDataGrid-row").length > 0);

		const hasPageHandlers = options.columns.find(col => col.pageHandler) != undefined;
		const hasApiHandlers = options.columns.find(col => col.apiHandler) != undefined;

		// apply page handlers rows and cells
		if (hasPageHandlers) {

			const form = gridElement.closest("form") as HTMLFormElement;

			if (form) {

				this.formService.applyPageHandlers(form);
			}
		}

		// apply api handlers rows and cells
		if (hasApiHandlers) {

			this.formService.applyApiHandlers(gridElement, () => {

				if (options.apiUrl) {

					refresh({ resetPageIndex: false });
				}
			});
		}

		// handle navigating to template URLs on row click
		const handleRowLinkTemplateClick = (event: MouseEvent) => {

			const target = event.target as HTMLElement;

			// ignore link clicks
			if (target.tagName === "A" || target.parentElement?.tagName === "A") {

				return;
			}

			if (state.data) {

				// attempt to get the row identifier
				const id = target.closest(".MuiDataGrid-row[data-id]")?.getAttribute("data-id");

				if (id) {

					// get the data item for the row identifier
					const keyField = options.keyField;
					const dataItem = state.data.find(item => getProperty(item, keyField) == id);

					// if there is a build row link template handler
					if (options.onBuildRowLinkTemplate) {

						// call the function by name and pass arguments
						const href = callFunction(options.onBuildRowLinkTemplate, {
							dataItem,
							options,
							urlService: this.urlService
						} as BuildRowLinkTemplateArgs) as string;

						// if a URL is returned navigate to it
						if (href) {

							window.location.href = href;
						}
					}
					else {

						// otherwise navigate to the formatted template URL
						window.location.href = urlDataFormat(options.rowLinkTemplate, dataItem);
					}
				}
			}
		};

		// if there is a row link template appy the grid-navigateable, to style the rows on hover state
		if (options.rowLinkTemplate && gridElement) {
			gridElement.addEventListener(EventConstants.click, handleRowLinkTemplateClick);
			gridElement.classList.add("grid-navigateable");
		}

		// return tear down function
		return () => {
			if (options.rowLinkTemplate && gridElement) {
				gridElement.removeEventListener(EventConstants.click, handleRowLinkTemplateClick);
			}
		};
	}

	/**
	 * Convert column models to MUI column definitions.
	 * @param input The data grid options, containing the column models to convert.
	 * @returns Array<GridEnrichedColDef>
	 * @see GridEnrichedColDef
	 */
	private convertColumns(input: DataGridOptions) {

		const columns = new Array<GridEnrichedColDef>();

		input.columns.forEach((column, index) => {

			const colDef = {
				field: column.field,
				headerName: column.title,
				width: column.width,
				sortable: column.sortable,
				hideSortIcons: true,
				disableColumnMenu: true,
				renderHeader: column.sortable ?
					colDef => MaterialDataGridService.getColumnSortHeader(colDef, input.getState?.()) :
					() => undefined
			} as GridEnrichedColDef;

			// apply column sort comparator, used with client data source mode
			if (column.sortField) {

				colDef.sortComparator = (v1: GridCellValue, v2: GridCellValue, cellParams1: GridSortCellParams, cellParams2: GridSortCellParams) => {

					const sortCol = input.columns.find(c => c.field === cellParams1.field);
					let sortValue1: GridCellValue;
					let sortValue2: GridCellValue;

					if (sortCol?.sortField) {

						sortValue1 = cellParams1.value;
						sortValue2 = cellParams2.value;
					}
					else {

						sortValue1 = v1;
						sortValue2 = v2;
					}

					if (sortValue1 && !sortValue2) {
						return 1;
					}
					else if (!sortValue1 && sortValue2) {
						return -1;
					}
					else if (sortValue1 && sortValue2) {
						return sortValue1 > sortValue2 ? 1 : -1;
					}
					else {
						return 0;
					}
				};
			}

			// apply calculated column types
			let columnType: DataGridColumnType;
			if (column.formattedField &&
				(column.displayType !== DataGridColumnType.HyperLink &&
					column.displayType !== DataGridColumnType.ConditionalClass && !column.conditionalClassName)) {

				columnType = DataGridColumnType.Formatted;
			}
			else if (column.conditionalClassName) {

				columnType = DataGridColumnType.ConditionalClass;
			}
			else {

				columnType = column.displayType;
			}

			// apply custom cell rendering by column type
			const renderer = MaterialDataGridService.cellRenderers.get(columnType);

			if (renderer) {

				colDef.renderCell = params => renderer(column, params);
			}

			if (index === (input.columns.length - 1)) {

				colDef.headerClassName = "col-cell-last";
			}

			// apply custom cell modification by column type
			const formatter = MaterialDataGridService.cellFormatters.get(column.displayType);

			if (formatter) {

				formatter(colDef);
			}

			// apply column class name to the header and the data cell of the MUI column definition
			if (column.className) {

				colDef.headerClassName = addClass(colDef?.headerClassName as string, column.className);
				colDef.cellClassName = addClass(colDef?.cellClassName as string, column.className);
			}

			columns.push(colDef);
		});

		return columns;
	}

	/**
	 * Auto size columns, takes the column width (applies default width of 100 if undefined),
	 * and then sets column widths proportionately to fill the available container width.
	 * @param columns The grid columns.
	 * @param elementId The container element ID.
	 * @param refresh A function used to refresh the data grid.
	 */
	private sizeColumns(columns: Array<GridEnrichedColDef>, elementId: string, refresh: () => void) {

		const container = document.getElementById(elementId);

		if (!container) return () => { };

		let tWidth: number = 0;
		const colWidths = new Array<number>();
		columns.forEach(column => {
			const width = column.width ?? 100;
			colWidths.push(width);
			tWidth += width;
		});

		// calculate the sizing proportion
		const sizingMultiplier = (container.clientWidth > 0 ? container.clientWidth : 100) / (tWidth + 20);

		// now size each column
		columns.forEach((column, index) => {
			column.width = sizingMultiplier * colWidths[index];
		});

		// grid redraw function for handling window resize
		let resizeTimeout: NodeJS.Timeout;
		const redraw = () => {
			if (resizeTimeout) clearTimeout(resizeTimeout);
			resizeTimeout = global.setTimeout(() => {
				refresh();
			}, 50);
		};

		// window resize event handler, which redraws data grid
		window.addEventListener("resize", redraw);

		// return tear down function
		return () => {
			window.removeEventListener("resize", redraw);
		};
	}

	/**
	 * Boolean column renderer, outputs a tick icon if the value equals true.
	 * @param column The data grid column options.
	 * @param params The data cell params.
	 */
	private static renderBool(column: DataGridColumnOptions, params: GridCellParams) {

		const boolValue = getTypedProperty<boolean>(params.row, params.field);
		if (boolValue === true) {

			return <i className="fas fa-check-circle text-success" title={column.title}></i>;
		}
		return <span></span>;
	}

	/**
	 * Negative boolean column renderer, outputs an error icon if the value equals true.
	 * @param column The data grid column options.
	 * @param params The data cell params.
	 */
	private static renderNegativeBool(column: DataGridColumnOptions, params: GridCellParams) {

		const boolValue = getTypedProperty<boolean>(params.row, params.field);
		if (boolValue === true) {

			return <i className="fas fa-times-circle text-danger" title={column.title}></i>;
		}
		return <span></span>;
	}

	/**
	 * Formatted column renderer, outputs a formatted field value, based on the formattedField of the column.
	 * @param column The data grid column options.
	 * @param params The data cell params.
	 */
	private static renderFormatted(column: DataGridColumnOptions, params: GridCellParams) {

		return <span>{getTypedProperty<string>(params.row, column.formattedField)}</span>
	}

	/**
	 * Hyperlink column renderer, outputs a hyperlink element.
	 * @param column The data grid column options.
	 * @param params The data cell params.
	 */
	private static renderHyperLink(column: DataGridColumnOptions, params: GridCellParams) {

		const href = urlDataFormat(column.linkTemplate, params.row);

		const link = {} as LinkProps;
		if (href) {

			link.href = MaterialDataGridService.urlServiceStatic.url(href);
		}

		if (column.target) {

			link.target = column.target;
		}

		if (column.tooltip) {

			link.title = column.tooltip;
		}

		if (column.linkCssClass) {

			link.className = column.linkCssClass;
		}

		let linkInnerContent;
		if (column.icon) {

			const ico = "fas " + column.icon;
			linkInnerContent = <i className={ico}></i>;
		}
		else if (column.label) {

			linkInnerContent = column.label;
		}

		if (!linkInnerContent) {

			linkInnerContent = getProperty(params.row, column.formattedField ?? params.field);
		}

		if (column.pageHandler) {

			const linkAny = link as any;
			const pageHandler = dataFormat(column.pageHandler, params.row);
			linkAny["data-page-handler"] = pageHandler;
			linkAny["formNoValidate"] = true;
			link.role = "button";
			link.tabIndex = 0;
		}
		else if (column.apiHandler) {

			const apih = $.extend(true, {}, column.apiHandler);
			apih.path = dataFormat(column.apiHandler.path, params.row);

			if (column.apiHandler.data) {

				for (const k in apih.data) {

					const value = apih.data[k];

					if (typeof value === "string") {

						apih.data[k] = dataFormat(value, params.row);
					}
				}
			}

			const linkAny = link as any;
			linkAny["data-api-handler"] = JSON.stringify(apih);
			linkAny["formNoValidate"] = true;
			link.role = "button";
			link.tabIndex = 0;
		}
		else if (column.urlField) {

			link.href = getProperty(params.row, column.urlField);

			if (!link.href) {

				return <React.Fragment></React.Fragment>
			}
		}

		// if this column link should be clicked when the row is clicked..
		if (column.activateOnRowClick) {

			const linkAny = link as any;
			linkAny["data-activate-on-row-click"] = true;
		}

		// apply tooltip wrapper around the link if renderTooltip equals true
		if (column.renderTooltip) {

			const text = getProperty(params.row, params.field);
			if (text) {

				link.className = addClass(link.className ?? "", "grid-text-tooltip");
				return <LightTooltip title={text} placement="right"><a {...(link) as any}>{linkInnerContent}</a></LightTooltip>;
			}
		}

		// if not a link, then apply the button role to the element
		if (!href && !column.activateOnRowClick) {

			link.role = "button";
		}

		// return link element
		return <a {...(link as any)}>{linkInnerContent}</a>;
	}

	/**
	 * Mail to column renderer, outputs a hyperlink element with an email mailto attribute.
	 * @param column The data grid column options.
	 * @param params The data cell params.
	 */
	private static renderMailTo(column: DataGridColumnOptions, params: GridCellParams) {

		const email = getProperty(params.row, column.field) as string;
		const mailTo = "mailto: " + email;
		return <a href={mailTo}>{email}</a>;
	}

	/**
	 * Tooltip column renderer, outputs a text with an accompanying tooltip.
	 * Useful for when text is truncated with ellipsis due to being too long
	 * @param column The data grid column options.
	 * @param params The data cell params.
	 */
	private static renderTooltip(column: DataGridColumnOptions, params: GridCellParams) {

		const text = getProperty(params.row, column.field) as string;
		return text ? <LightTooltip title={text} placement="right"><span className="grid-text-tooltip">{text}</span></LightTooltip> : <span></span>;
	}

	/**
	 * Conditional CSS class column renderer, outputs a value wrapped with a class if the configured condition is met.
	 * @param column The data grid column options.
	 * @param params The data cell params.
	 */
	private static renderConditionalClass(column: DataGridColumnOptions, params: GridCellParams) {

		const text = getProperty(params.row, column.formattedField ?? column.field) as string;
		let cssClass: string | null;

		if (column.conditionalClassName.rule == Constants.NegativeValueRuleName) {

			const value = getProperty(params.row, column.conditionalClassName.field ?? column.field) as number;
			cssClass = value < 0 ? column.conditionalClassName.className : null;
		}
		else if (column.conditionalClassName.rule == Constants.PositiveValueRuleName) {

			const value = getProperty(params.row, column.conditionalClassName.field ?? column.field) as number;
			cssClass = value > 0 ? column.conditionalClassName.className : null;
		}
		else if (column.conditionalClassName.rule == Constants.HasNumericValueRuleName) {

			const value = getProperty(params.row, column.conditionalClassName.field ?? column.field) as number;
			cssClass = (value ?? 0) != 0 ? column.conditionalClassName.className : null;
		}
		else if (column.conditionalClassName.field) {

			const value = getProperty(params.row, column.conditionalClassName.field) as boolean;
			cssClass = value === true ? column.conditionalClassName.className : null;
		}
		else {

			cssClass = null;
		}

		return cssClass ? <span className={cssClass}>{text}</span> : <span>{text}</span>;
	}

	/**
	 * Badge column renderer, outputs column text as a Bootstrap badge element.
	 * @param column The data grid column options.
	 * @param params The data cell params.
	 */
	private static renderBadge(column: DataGridColumnOptions, params: GridCellParams) {

		const text = getProperty(params.row, column.field) as string;
		const badgeCssClass = getProperty(params.row, column.badgeCssClassField) as string;
		const fullCssClass = badgeCssClass ? `badge badge-${badgeCssClass}` : null;
		return fullCssClass ? <span className={fullCssClass}>{text}</span> : <span>{text}</span>;
	}

	/**
	 * HTML column renderer, outputs HTML directly.
	 * @param column The data grid column options.
	 * @param params The data cell params.
	 */
	private static renderHtml(column: DataGridColumnOptions, params: GridCellParams) {

		const value = getProperty(params.row, column.field) as string;
		return <span dangerouslySetInnerHTML={{ __html: value }}></span>;
	}

	/**
	 * Icon column renderer, outputs an icon element.
	 * @param column The data grid column options.
	 * @param params The data cell params.
	 */
	private static renderIcon(column: DataGridColumnOptions, params: GridCellParams) {

		const icon = getProperty(params.row, column.field) as Icon;
		return <i className={icon.className} title={icon.tooltip || column.title}></i>;
	}

	/**
	 * Financial column column modifier, applies financial CSS class.
	 * @param column The data column definition.
	 */
	private static formatFinancial(column: GridEnrichedColDef) {

		column.headerClassName = addClass(column?.cellClassName as string, Constants.CssClassFinancial);
		column.cellClassName = addClass(column?.cellClassName as string, Constants.CssClassFinancial);
	}

	/**
	 * Icon column column modifier, applies icon CSS class.
	 * @param column The data column definition.
	 */
	private static formatIcon(column: GridEnrichedColDef) {

		column.headerClassName = addClass(column?.cellClassName as string, MaterialDataGridService.iconCellClass);
		column.cellClassName = addClass(column?.cellClassName as string, MaterialDataGridService.iconCellClass);
	}

	/**
	 * Gets the column sort header element.
	 * @param params The data column header params.
	 * @param state The data grid state.
	 * @returns Column header title and icon with a tooltip.
	 */
	private static getColumnSortHeader(params: GridColumnHeaderParams, state?: DataGridState) {

		const colDef = params.colDef;
		const sortField = state?.sortModel.find(s => s.field === colDef.field);
		const sortDir = sortField?.sort ?? "off";
		const sortDirIcon = sortField?.sort === "desc" ? "fa-sort-amount-down" : "fa-sort-amount-up-alt";
		let iconClass = `fas ${sortDirIcon} col-sort-icon col-sort-${sortDir}`;
		let tooltipTitle;

		switch (sortDir) {
			case "asc":
				tooltipTitle = `Click to sort by ${colDef.headerName ?? colDef.field} descending`;
				break;
			case "desc":
				tooltipTitle = "Click to remove sorting";
				break;
			default:
				tooltipTitle = `Click to sort by ${colDef.headerName ?? colDef.field} ascending`;
				break;
		}

		// return column header title and icon with a tooltip
		return <LightTooltip title={tooltipTitle} placement="right">
			<div className="col-sort-wrapper">
				<div className="MuiDataGrid-colCellTitle">{colDef.headerName}</div>
				<i className={iconClass}></i>
			</div>
		</LightTooltip>;
	}

	/**
	 * Converts a sort model to the MUI equivalent.
	 * @param input The input field sorts.
	 * @param columns The array of column options.
	 */
	private convertSortModel(input: Array<FieldSort>, columns: Array<DataGridColumnOptions>) {

		return (input ? input.map(sort => {
			const column = this.getColumn(columns, sort.field);
			return {
				field: column?.field ?? sort.field,
				sort: (sort.direction == ListSortDirection.Descending ? "desc" : "asc")
			}
		}) : []) as GridSortModel;
	}

	/**
	 * Finds a column by field name.
	 * @param columns The array of columns to search.
	 * @param field The field name to match.
	 * @returns DataGridColumnOptions or null if not matched.
	 * @see DataGridColumnOptions
	 */
	private getColumn(columns: Array<DataGridColumnOptions>, field: string) {

		const lowered = field.toLowerCase();
		return columns.find(col => col.field.toLowerCase() === lowered);
	}

	/**
	 * Gets the data for the grid, when data source mode is server bound.
	 * @param apiUrl the API URL to query.
	 * @param options The data grid options.
	 * @param state the current data grid state.
	 * @returns Promise of any data type.
	 */
	private getData(apiUrl: ApiUrl, options: DataGridOptions, state: DataGridState) {

		// build the URL
		const url = this.urlService.apiUrl(apiUrl);

		let apiModel: ApiModel | undefined;
		if ((apiModel = options.apiModel)) {

			// apply paging to request model
			if (apiModel.paging && state.page >= 0 && state.pageSize > 0) {

				apiModel.paging.page = state.page + 1;
				apiModel.paging.pageSize = state.pageSize;
			}

			// apply sorting to request model
			if (state.sortModel) {

				apiModel.sorts = state.sortModel.map(sortItem => {
					return {
						direction: sortItem.sort === "desc" ? ListSortDirection.Descending : ListSortDirection.Ascending,
						field: sortItem.field
					};
				});
			}

			// apply filtering to request model
			if (options.filtersElementId) {

				const filtersElement = document.getElementById(options.filtersElementId);

				if (filtersElement) {
					this.filterService.applyFilters(filtersElement, apiModel.filters ?? apiModel, apiModel);
				}
			}
		}

		// perrform AJAX request to get data
		return this.ajaxService.request<any>({
			url,
			method: apiUrl.method,
			data: apiModel
		});
	}

	/**
	 * Applies financial column alignment to financial column cells.
	 * @param gridElement The data grid element.
	 */
	private applyFinancialColumnAlignment(gridElement: Element) {

		// check for finanical column cells
		const financialCols = gridElement.querySelectorAll(".MuiDataGrid-virtualScrollerRenderZone .financial");
		if (financialCols.length > 0) {

			let maxWidth: number | undefined;
			const valueElements = new Array<HTMLSpanElement>();
			financialCols.forEach(element => {

				const cell = element as HTMLDivElement;

				// if the cell has text
				if (cell.innerText) {

					// create new value element, wrapping the text or pickup previously created one and re-use
					const valueElement = cell.querySelector("span") ?? document.createElement("span");
					valueElement.classList.add("cell-decimal-value");
					valueElement.innerHTML = cell.innerText;

					// clear the previously width if any
					if (valueElement.style.width) {
						valueElement.style.width = "";
					}

					// check for a link, if there is one place the created value element inside it
					const link = cell.querySelector("a");
					if (link) {

						link.innerHTML = "";
						link.appendChild(valueElement);
					}
					else {

						// otherwise just clear cell text and place the created value element inside the cell directly
						cell.innerHTML = "";
						cell.appendChild(valueElement);
					}
										
					valueElements.push(valueElement);

					// keep a reference the maximum value element width
					const valueElementWidth = valueElement.getBoundingClientRect().width;
					if (!maxWidth || maxWidth < valueElementWidth) {
						maxWidth = valueElementWidth;
					}
				}

				cell.style.visibility = "visible";
			});

			// if there's a max width
			if (maxWidth) {

				// loop value elements and set each one to the max width
				valueElements.forEach(element => {
					const valueElement = element as HTMLSpanElement;
					valueElement.style.width = `${maxWidth}px`;
				});
			}
		}
	}

	/**
	 * Applies detail row content to the configured row.
	 * @param options The data grid options.
	 * @param state The current data grid state.
	 * @param gridElement The data grid target element.
	 */
	private applyDetailRow(options: DataGridOptions, state: DataGridState, gridElement: Element) {

		// if detail row key, detail row element ID and key field are set,
		// then detail row content should attempt to be inserted
		if (!isNullOrUndefined(options.detailRowKey) && options.detailRowElementId && options.keyField) {

			// get the detail row content element
			const detailRowElement = document.getElementById(options.detailRowElementId);

			if (detailRowElement) {

				// clear any existing sub row element
				$(gridElement).find(".sub-row").remove();

				// loop row elements
				gridElement.querySelectorAll(".MuiDataGrid-virtualScrollerRenderZone .MuiDataGrid-row").forEach(rowElement => {

					const id = rowElement.getAttribute("data-id");
					const dataItem = state.data.find(item => getProperty(item, options.keyField) == id);
					let hasChildRow = false;
					const keyValue = getProperty(dataItem, options.keyField);

					// check the data item key equals the configured detail row key
					if (keyValue === options.detailRowKey) {

						// now create the container sub row element and add to the grid
						const detailRowContainer = document.createElement("div");
						detailRowContainer.classList.add("sub-row");
						detailRowContainer.appendChild(detailRowElement);

						rowElement.after(detailRowContainer);
						rowElement.classList.add("expanded");

						// ensure styles are compatible
						const renderingZone = rowElement.closest(".MuiDataGrid-virtualScrollerRenderZone") as HTMLElement;
						if (renderingZone) {
							renderingZone.style.removeProperty("max-height");
							renderingZone.style.removeProperty("width");
						}
						const gridViewPort = rowElement.closest(".MuiDataGrid-virtualScrollerContent") as HTMLElement;
						if (gridViewPort) gridViewPort.style.removeProperty("height");
						const gridWindow = rowElement.closest(".MuiDataGrid-virtualScrollerRenderZone") as HTMLElement;
						if (gridWindow) {
							gridWindow.style.removeProperty("max-height");
							gridWindow.style.removeProperty("overflow-y");
							gridWindow.style.position = "static";
							gridWindow.style.removeProperty("top");
						}
						const gridRoot = rowElement.closest(".MuiDataGrid-root") as HTMLElement;
						if (gridRoot) gridRoot.style.removeProperty("height");

						hasChildRow = true;
					}

					// ensure other rows can be clicked with page handler called
					if (hasChildRow === false && options.rowPageHandler) {

						rowElement.setAttribute("data-page-handler", dataFormat(options.rowPageHandler, dataItem));
					}
				});
			}
		}
	}

	/**
	 * Applies row level hyperlink elements from column cell hyperlinks.
	 * @param options The data grid options.
	 * @param state The current data grid state.
	 * @param gridElement The data grid target element.
	 */
	private applyActivateOnRowClick(options: DataGridOptions, state: DataGridState, gridElement: Element) {

		gridElement.querySelectorAll("[data-activate-on-row-click]").forEach(element => {

			const rowElement = element.closest(".MuiDataGrid-row") as HTMLElement;

			if (rowElement) {

				// apply page handler to the row
				const pageHandler = element.getAttribute("data-page-handler");
				if (pageHandler) {

					const id = rowElement.getAttribute("data-id");
					const dataItem = state.data.find(item => getProperty(item, options.keyField) == id);
					const keyValue = getProperty(dataItem, options.keyField);
					if (keyValue !== Guid.empty) {
						rowElement.setAttribute("data-page-handler", pageHandler);
					}
					return;
				}

				// apply api handler to the row
				const apiHandler = element.getAttribute("data-api-handler");
				if (apiHandler) {

					rowElement.setAttribute("data-api-handler", apiHandler);
					return;
				}

				const href = element.getAttribute("href");
				if (href) {

					// set data-href on row to that of the link href
					rowElement.setAttribute("data-href", href);

					// add click event handler on the row element
					rowElement.addEventListener("click", event => {

						const target = event.target as HTMLElement;

						// ignore link clicks
						if (target.tagName === "A" || target.parentElement?.tagName === "A") {

							return;
						}

						// check for link target
						const link = rowElement.querySelector("a[data-activate-on-row-click]") as HTMLAnchorElement;
						if (link.target === "_blank") {

							// open in new window
							window.open(href);
							return;
						}

						// navigate to the href
						window.location.href = href;
					});
				}
			}
		});
	}

	/**
	 * Applies conditional row CSS class.
	 * @param options The data grid options.
	 * @param state The current data grid state.
	 * @param gridElement The data grid target element.
	 */
	private applyRowClass(options: DataGridOptions, state: DataGridState, gridElement: Element) {

		if (!options.conditionalRowClassName?.className) {
			return;
		}

		// loop the row elements
		gridElement.querySelectorAll(".MuiDataGrid-virtualScrollerRenderZone .MuiDataGrid-row").forEach(rowElement => {

			const id = rowElement.getAttribute("data-id");
			const dataItem = state.data.find(item => getProperty(item, options.keyField) == id);
			const value = getProperty(dataItem, options.conditionalRowClassName.field);

			// apply CSS class if rule is equality and the values match

			if (options.conditionalRowClassName.rule === Constants.EqualityRuleName) {

				if (options.conditionalRowClassName.value === value) {

					rowElement.classList.add(options.conditionalRowClassName.className);
				}
				else {

					rowElement.classList.remove(options.conditionalRowClassName.className);
				}
			}
		});
	}
}